/*

  Types.ts

  Common type definitions for the Explo/Netskrafl user interface

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { VnodeChildren } from "./mithril";
import { GlobalState } from "./globalstate";

// Global constants

export const RACK_SIZE = 7;
export const ROWIDS = "ABCDEFGHIJKLMNO";
export const BOARD_SIZE = ROWIDS.length;
export const EXTRA_WIDE_LETTERS = "q";
export const WIDE_LETTERS = "zxmæ";
export const ZOOM_FACTOR = 1.5;

export const ERROR_MESSAGES: { [key: string]: string } = {
  // Translations are found in /static/assets/messages.json
  1: "Enginn stafur lagður niður",
  2: "Fyrsta orð verður að liggja um byrjunarreitinn",
  3: "Orð verður að vera samfellt á borðinu",
  4: "Orð verður að tengjast orði sem fyrir er",
  5: "Reitur þegar upptekinn",
  6: "Ekki má vera eyða í orði",
  7: "word_not_found",
  8: "word_not_found",
  9: "Of margir stafir lagðir niður",
  10: "Stafur er ekki í rekkanum",
  11: "Of fáir stafir eftir, skipting ekki leyfð",
  12: "Of mörgum stöfum skipt",
  13: "Leik vantar á borðið - notið F5/Refresh",
  14: "Notandi ekki innskráður - notið F5/Refresh",
  15: "Rangur eða óþekktur notandi",
  16: "Viðureign finnst ekki",
  17: "Viðureign er ekki utan tímamarka",
  18: "Netþjónn gat ekki tekið við leiknum - reyndu aftur",
  19: "Véfenging er ekki möguleg í þessari viðureign",
  20: "Síðasti leikur er ekki véfengjanlegur",
  21: "Aðeins véfenging eða pass leyfileg",
  "server": "Netþjónn gat ekki tekið við leiknum - reyndu aftur"
};

// Items in a list of users
export interface UserListItem {
  rank: number;
  rank_yesterday: number;
  rank_week_ago: number;
  userid: string;
  nick: string;
  fullname: string;
  inactive: boolean;
  chall: boolean;
  fairplay: boolean;
  ready: boolean;
  ready_timed: boolean;
  fav: boolean;
  ratio: number;
  avgpts: number;
  games: number;
  games_yesterday: number;
  games_week_ago: number;
  games_month_ago: number;
  elo: number;
  human_elo: number;
  elo_yesterday: number;
  elo_week_ago: number;
  elo_month_ago: number;
}

// Items in a game list
export interface GameListItem {
  uuid: string;
  fullname: string;
  my_turn: boolean;
  timed: boolean;
  prefs: { manual: boolean; };
  zombie: boolean;
  overdue: boolean;
  oppid: string;
  opp: string;
  sc0: number;
  sc1: number;
  url: string;
  tile_count: number;
  ts: string;
}

// Items in a list of recent games
export interface RecentListItem {
  opp: string;
  opp_is_robot: boolean;
  sc0: number;
  sc1: number;
  ts_last_move: string;
  prefs: {
    manual: boolean;
    duration: number;
  };
  days: number;
  hours: number;
  minutes: number;
  elo_adj: number;
  human_elo_adj: number;
  url: string;
}

// Items in a list of challenges (sent or received)
export interface ChallengeListItem {
  key: string;
  userid: string;
  received: boolean;
  opp: string;
  fullname: string;
  prefs: {
    manual: boolean;
    fairplay: boolean;
    timed: boolean;
    duration: number;
  };
  ts: string;
  opp_ready: boolean;
  live: boolean;
  image: string;
  fav: boolean;
}

// User preferences, typically edited in a modal dialog
export interface UserPrefs {
  nickname: string;
  full_name: string;
  email: string;
  beginner: boolean;
  fairplay: boolean;
  audio: boolean;
  fanfare: boolean;
  logout_url: string;
  // The following is currently read-only, i.e. only displayed
  // and not directly modified in the user preferences dialog
  friend: boolean;
}

// Information about a move in a move list
export interface MoveInfo {
  leftTotal: number;
  rightTotal: number;
  player: 0 | 1;
  co: string;
  tiles: string;
  score: number;
}

export interface MovesRequest {
  locale: string;
  board_type: string;
  board: string[];
  rack: string;
  limit: number;
}

export interface MoveWithScore {
  co: string;
  w: string;
  sc: number;
}

// The data returned from the /bestmoves API endpoint
export interface BestMoves {
  version: string;
  count: number;
  moves: MoveWithScore[];
}

export interface UserErrors {
  nickname?: string;
  full_name?: string;
  email?: string;
}

export interface TileData {
  player: 0 | 1;
  tile: string;
  letter: string;
  score: number;
  freshtile: boolean;
  draggable: boolean;
  review?: boolean;
  index: number;
  xchg: boolean;
  highlight?: 0 | 1;
}

export interface UserStats extends Record<string, any> {
  favorite?: boolean;
  friend?: boolean;
}

export type TileDict = { [index: string]: TileData; };

export type RackTile = [
  tile: string,
  score: number
];

export interface SavedTile {
  sq: string;
  tile: string;
}

export type TileScoreDict = { [index: string]: number; };

export type Move = [
  player: 0 | 1,
  summary: [coord: string, tiles: string, score: number],
  highlighted: boolean,
];

export type MoveDetail = [string];

export interface MoveListener {
  notifyMove: () => void;
  notifyGameWon: () => void;
}

export interface Message {
  from_userid: string;
  msg: string;
  ts: string;
}  

export type TwoLetterTuple = [string, string[]];
export type TwoLetterLists = [TwoLetterTuple[], TwoLetterTuple[]];

export interface ServerGame {
  result: number;
  msg: string;
  newmoves: Move[];
  two_letter_words: TwoLetterLists;
  num_moves: number;
  // Several other properties are also sent from the server,
  // but they are copied using key/value enumeration
}

// Information about a Gáta Dagsins riddle, received from the server
export interface ServerRiddle {
  date: string;
  board: string[]; // 15x15 board state
  rack: RackTile[]; // Tiles in the rack
  max_score: number; // Theoretical maximum score
  alphabet: string;
  tile_scores: TileScoreDict;
  two_letter_words: TwoLetterLists;
  board_type: "standard" | "explo";
}

export interface RiddleWord {
  // A riddle word submitted to the backend by the player
  word: string;
  score: number;
  coord: string;
  timestamp: string;
}

export interface RiddleScores {
  global_best: number;
  group_best: number;
  personal_best: number;
  player_moves: RiddleWord[];
}

export type ChallengeAction = "issue" | "retract" | "decline" | "accept";

export type DialogViewEnum = "userprefs" | "userinfo" | "challenge" | "friend" | "wait" | "accept";

export interface ChallengeParameters {
  action: ChallengeAction;
  destuser: string;
  duration?: number;
  fairplay?: boolean;
  manual?: boolean;
  key?: string;
}

export interface ChatMessage {
  from_userid: string;
  game: string;
  msg: string;
  ts: string;
}

export interface UserListCriteria {
  query: string;
  spec: string;
}

export type EloListSelection = "human" | "all" | "manual";

export interface StatsType extends Record<string, any> {
  scores: [number, number];
  bingoes0: number;
  bingoes1: number;
}

export interface BestScore {
  score: number;
  player: string;
  word: string; // May contain '?x' for blanks
  coord: string; // Starting coordinate of the word
  timestamp: string; // Timestamp of the score
}

// Base game interface with common properties and methods
export interface IBaseGame {
  // Core game identification
  uuid: string;
  
  // Static game attributes
  locale: string;
  alphabet: string;
  startSquare: string;
  manual: boolean;
  tile_scores: TileScoreDict;

  // Transient game state
  tiles: TileDict;
  over: boolean;
  currentScore: number | undefined;
  currentWord: string;
  currentCoord: string;
  localturn: boolean;
  wordGood: boolean;
  wordBad: boolean;
  currentError: string | number | null;
  currentMessage: string | null;
  moveInProgress: boolean; // True when waiting for server response (Netskrafl only, default false)

  // UI state
  showingDialog: string | null;
  selectedSq: string | null;
  askingForBlank: { from: string; to: string; } | null;

  // Common methods
  buttonState: () => Record<string, boolean>;
  twoLetterWords: () => TwoLetterLists;
  attemptMove: (from: string, to: string) => void;
  placeBlank: (letter: string) => void;
  cancelBlankDialog: () => void;
  squareClass: (coord: string) => string | undefined;
  resetRack: () => void;
  rescrambleRack: () => void;
  resetError: () => void;
  placeTiles: (move?: number, noHighlight?: boolean) => void;
  tilesPlaced: () => string[];
  tilescore: (tile: string) => number;
  allowDragDrop: () => boolean;
  cleanup: () => void;
}

// Netskrafl-specific game interface
export interface IGame extends IBaseGame {
  // Multiplayer-specific properties
  userid: [string, string];
  autoplayer: [boolean, boolean];
  nickname: [string, string];
  fullname: [string, string];
  player: number | null; // null means that the current user is not a participant
  moves: Move[];
  moveInProgress: boolean;
  last_chall: boolean;

  // Chat and social
  chatLoading: boolean;
  congratulate: boolean;
  messages: Message[] | null;
  chatSeen: boolean;

  // Game mechanics
  bag: string;
  newbag: boolean;
  fairplay: boolean;
  stats: StatsType | null | undefined;

  // Timing (for timed games)
  clockText0: string;
  clockText1: string;
  runningOut0: boolean;
  runningOut1: boolean;
  blinking0: boolean;
  blinking1: boolean;

  // Netskrafl-specific methods
  showClock: () => boolean;
  displayScore: (player: 0 | 1) => number;
  isRobotGame: () => boolean;
  submitResign: () => void;
  submitChallenge: () => void;
  submitMove: () => void;
  submitPass: () => void;
  submitExchange: () => void;
  forceResign: () => Promise<void>;
  sendMessage: (msg: string) => Promise<void>;
  markChatShown: () => boolean;
  loadStats: () => Promise<void>;
  setRack: (rack: RackTile[]) => void;
  rackAtMove: (moveIndex: number) => string;
  isFinalMove: (moveIndex: number) => boolean;
  boardAsStrings: () => string[];
  notifyUserChange: (nickname: string) => void;
  addChatMessage: (from_userid: string, msg: string, ts: string, ownMessage: boolean) => void;
  update: (srvGame: ServerGame) => void;
  confirmResign: (yes: boolean) => void;
  confirmChallenge: (yes: boolean) => void;
  confirmPass: (yes: boolean) => void;
  confirmExchange: (yes: boolean) => void;
}

// Gáta Dagsins-specific game interface
export interface IRiddle extends IBaseGame {
  // Daily puzzle properties
  date: string;
  model: IModel;

  // Scoring properties, static
  bestPossibleScore: number;
  warmBoundary: number; // Score boundary for warm zone
  hotBoundary: number; // Score boundary for hot zone

  // Scoring properties, dynamic
  globalBestScore: BestScore | null;
  groupBestScore: BestScore | null;
  personalBestScore: number;
  playerMoves: RiddleWord[];

  // Gáta Dagsins-specific methods
  load: (date: string, locale: string) => Promise<void>;
  recreateWordOnBoard: (word: string, startCoord: string) => void;
  updateGlobalBestScore: (best: BestScore) => void;
  updateGroupBestScore: (best: BestScore) => void;
}

export interface IModel {
  state: GlobalState | null;

  game: IGame | null; // Netskrafl game instance
  riddle: IRiddle | null; // Gáta Dagsins riddle instance

  // Gáta Dagsins-specific properties
  userStats: UserRiddleStats | null;
  leaderboard: LeaderboardEntry[];

  // Existing Netskrafl-specific properties
  numGames: number;
  numChallenges: number;
  oppReady: number;
  gameList: GameListItem[] | null;
  loadingAllLists: boolean;
  loadingGameList: boolean;
  maxFreeGames: number;
  ownStats: Record<string, any> | null;
  challengeList: ChallengeListItem[] | null;
  recentList: RecentListItem[] | null;
  userListCriteria: UserListCriteria | null | undefined;
  userList: UserListItem[] | null | undefined;
  eloRatingSpec: EloListSelection | null | undefined;
  eloRatingList: UserListItem[] | null | undefined;
  reviewMove: number | null;
  bestMoves: Move[] | null;
  highlightedMove: number | null;
  user: UserPrefs | null | undefined;
  userErrors: UserErrors | null;
  userLoadError: boolean;

  // Common methods
  moreGamesAllowed: () => boolean;
  loadUser: (activateSpinner: boolean) => Promise<void>;
  saveUser: (successFunc: () => void) => Promise<void>;
  
  // Netskrafl-specific methods
  loadAllLists: (includesZombies?: boolean) => Promise<void>;
  loadGameList: (includesZombies?: boolean) => Promise<void>;
  loadChallengeList: () => Promise<void>;
  loadRecentList: () => Promise<void>;
  loadUserList: (criteria: UserListCriteria) => Promise<void>;
  loadEloRatingList: (spec: EloListSelection) => Promise<void>;
  loadOwnStats: () => Promise<void>;
  loadUserStats: (userid: string, readyFunc: (json: UserStats) => void) => Promise<void>;
  loadUserRecentList: (userid: string, versus: string | null, readyFunc: (json: any) => void) => Promise<void>;
  loadPromoContent: (key: string, readyFunc: (html: string) => void) => Promise<void>;
  loadBestMoves: (moveIndex: number) => Promise<void>;

  // Gáta Dagsins-specific methods
  initRiddle: (date: string, locale: string) => Promise<void>;
}

// Interface for Actions methods that View needs to access
export interface IActions {
  model: IModel;
  handleChallenge: (parameters: ChallengeParameters) => Promise<void>;
  acceptChallenge: (destuser: string, key: string) => Promise<void>;
  declineChallenge: (destuser: string, key: string) => Promise<void>;
  retractChallenge: (destuser: string, key: string) => Promise<void>;
  startNewGame: (oppid: string, reverse?: boolean) => Promise<void>;
  startRobotGame: (oppid: string) => Promise<void>;
  loadUsers: (criteria: UserListCriteria, activateSpinner?: boolean) => Promise<void>;
  searchUsers: (searchTerm: string, activateSpinner?: boolean) => Promise<void>;
  loadUsersByType: (listType: string, activateSpinner?: boolean) => Promise<void>;
  toggleFavorite: (userId: string, currentStatus: boolean) => Promise<void>;
  markFavorite: (userId: string, status: boolean) => Promise<void>;
  showSpinner: () => void;
  hideSpinner: () => void;
  initMediaListener: (view: IView) => void;
  setUserPref: (pref: Record<string, any>) => Promise<void>;
  cancelFriendship: () => Promise<boolean>;
  withSpinner<T>(asyncAction: () => Promise<T>): Promise<T>;
  attachListenerToRiddle: (date: string, locale: string) => void;
  detachListenerFromRiddle: (date: string, locale: string) => void;
  fetchRiddle: (date: string, locale: string) => Promise<void>;
  cleanupRiddle: (date: string, locale: string) => void;
}

export interface IView {
  model: IModel;
  actions: IActions;
  boardScale: number;
  selectedTab: string;
  notifyMediaChange: () => void;
  notifyChatMessage: () => void;
  pushDialog: (dialogName: DialogViewEnum, dialogArgs?: any) => void;
  popDialog: () => void;
  popAllDialogs: () => void;
  showUserInfo: (userid: string, nick: string, fullname: string) => void;
  showFriendPromo: () => void;
  showAcceptDialog: (oppId: string, oppNick: string, challengeKey: string) => void;
  setSelectedTab: (tabid: string) => boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  updateScale: () => void;
  resetScale: () => void;
  isDialogShown: () => boolean;
  vwDialogs: () => VnodeChildren;
  appView: (routeName: string) => VnodeChildren;
}

export interface IPlayerMove {
  score: number;
  isGlobalBestScore?: boolean; // Optional flag for global best score
  isGroupBestScore?: boolean; // Optional flag for group best score
  // Note: word and coord can be empty if this is the top group score
  // but belongs to another player
  word: string;
  coord: string;
  timestamp?: string; // ISO timestamp for when the move was made
}

// User statistics for Gáta Dagsins streaks and totals
// Path: /gatadagsins/users/{locale}/{userId}/stats
export interface UserRiddleStats {
  currentStreak: number;      // Current consecutive days played
  longestStreak: number;      // Best ever consecutive days
  topScoreStreak: number;     // Current consecutive days with perfect score
  lastPlayedDate: string;     // ISO date of last play (YYYY-MM-DD)
  totalDaysPlayed: number;    // Total number of days played
  totalTopScores: number;     // Total perfect scores achieved
}

// Daily leaderboard entry for Gáta Dagsins
// Path: /gatadagsins/{date}/{locale}/leaders/{userId}
export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  score: number;
  timestamp: string;          // ISO timestamp
}
